import {
  type BroughtFiles,
  checkedChartPage,
  collectedAtFor,
  type ExtractedAllergy,
  type ExtractedChartPage,
  type ExtractedDemographics,
  type ExtractedFamilyHistory,
  type ExtractedFile,
  type ExtractedHealthCard,
  type ExtractedImmunization,
  type ExtractedLabResult,
  type ExtractedMedication,
  type ExtractedProblem,
  type ExtractedSocialHistory,
  type ExtractedVisit,
  type NotImportedItem,
  type PatientAdministrativeSex,
} from "./extraction";
import type { CapturedFile, CapturedFiles } from "./handoff";
import {
  blockText,
  clean,
  controlValue,
  type LabelledControl,
  labelKey,
  labelledControls,
  ownText,
  parseCapturedPage,
  readDate,
} from "./pageReading";

/**
 * TELUS CHR's patient chart, read from what CHR draws for it: the chart's
 * heading (name, birth date, sex and primary health card), the patient tags
 * under it, the sections of its Summary (chart entries, lab results, every
 * encounter's note, the list of files) and, when the physician has them open,
 * the Update patient form and the health card's Update identification form
 * (the only place CHR shows the card's expiry, as its plan end date).
 *
 * CHR's Summary has no headings, and its tables draw their column titles apart
 * from their rows, so a section is found by CHR's own name for it
 * (`.summary-item.medications`) and a cell by its field (`td.start_at`), the
 * names CHR's Summary settings use; forms are read by their labels. Checked
 * against a CHR test patient's chart in October 2026; the fixture
 * (`fixtures/telus-chr-synthetic.html`) keeps that chart's structure with
 * invented values.
 */

/** The chart CHR shows beside its "Opened patients" list. */
const CHART = [
  ".patient-chart-content-view.visible-patient-contents",
  ".patient-chart-content-view",
];

const PROVINCES = new Set([
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
]);

/** A cell that says nothing: CHR writes "N/A" for an open-ended date. */
const NOTHING = /^(n\/a|none|-+|—)$/iu;

function shown(text: string | null | undefined): string | undefined {
  const value = clean(text);
  return value === "" || NOTHING.test(value) ? undefined : value;
}

/** The object without its undefined members, for an extracted row's optional fields. */
function defined<T extends Record<string, unknown>>(
  value: T,
): { [K in keyof T]?: Exclude<T[K], undefined> } {
  return Object.fromEntries(Object.entries(value).filter(([, member]) => member !== undefined)) as {
    [K in keyof T]?: Exclude<T[K], undefined>;
  };
}

function noteFrom(parts: readonly string[]): string | undefined {
  return parts.length === 0 ? undefined : parts.join("; ").slice(0, 2000);
}

/** The capture's day in Ontario, to tell a finished prescription from a current one. */
function captureDay(capturedAt: string | undefined): string {
  const moment = new Date(capturedAt ?? Date.now());
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(Number.isNaN(moment.getTime()) ? new Date() : moment);
}

// ---------------------------------------------------------------------------
// The patient
// ---------------------------------------------------------------------------

const PATIENT_DETAILS = "Patient details";

function sexFrom(value: string): PatientAdministrativeSex | undefined {
  const word = value.trim().toLowerCase();
  if (/^(f|female|woman)$/.test(word)) return "female";
  if (/^(m|male|man)$/.test(word)) return "male";
  if (/^(x|other|non-binary|intersex)$/.test(word)) return "other";
  if (/^(u|unknown|undisclosed|not specified|prefer not to (say|disclose))$/.test(word))
    return "unknown";
  return undefined;
}

interface Heading {
  readonly name?: string | undefined;
  readonly dateOfBirth?: string | undefined;
  readonly sex?: string | undefined;
  readonly genderIdentity?: string | undefined;
  readonly card?: { readonly type: string; readonly value: string } | undefined;
}

function chartHeading(chart: Element): Heading {
  const header = chart.querySelector(".patient-chart-header");
  if (header === null) return {};
  const text = (selector: string) => shown(header.querySelector(selector)?.textContent);
  const type = text("[data-testid='primary-identification-name']")?.replace(/:$/u, "");
  const value = text("[data-testid='primary-identification-value']");
  return {
    name: text("[data-testid='name-variants'], .patient-name"),
    dateOfBirth: text("[data-testid='patient-date-of-birth']"),
    sex: text("[data-testid='patient-gender']"),
    genderIdentity: text("[data-testid='patient-gender-identity'] > span"),
    card: type === undefined || value === undefined ? undefined : { type, value },
  };
}

/** CHR's heading writes the name first name first, without a middle name: "Casey Testpatient". */
function headingName(
  name: string,
): Pick<ExtractedDemographics, "legalFirstName" | "legalLastName"> {
  const words = clean(name.replace(/\([^)]*\)|"[^"]*"/gu, " "))
    .split(" ")
    .filter(Boolean);
  const last = words.at(-1);
  if (last === undefined) return {};
  return words.length === 1
    ? { legalLastName: last }
    : { legalFirstName: words.slice(0, -1).join(" "), legalLastName: last };
}

interface Identification {
  readonly type: string;
  readonly value: string;
  readonly versionCode?: string | undefined;
  readonly primary: boolean;
}

interface PatientForm {
  readonly fields: ReadonlyMap<string, LabelledControl>;
  readonly identifications: readonly Identification[];
}

function updatePatientForm(page: Document): PatientForm | undefined {
  const form = page.querySelector("form.respondent-information");
  if (form === null) return undefined;
  const identifications = Array.from(form.querySelectorAll(".double-input")).flatMap((row) => {
    const type = shown(row.querySelector(".double-input__read-only-input")?.textContent);
    const value = shown(
      controlValue(
        row.querySelector("input[data-testid='identification-input']") ??
          row.querySelector(".double-input__primary-input input"),
      ),
    );
    if (type === undefined || value === undefined) return [];
    return [
      {
        type,
        value,
        versionCode: shown(controlValue(row.querySelector("input[name='version_code']"))),
        primary: Array.from(row.querySelectorAll(".tag__label-text")).some(
          (tag) => clean(tag.textContent) === "Primary",
        ),
      },
    ];
  });
  return { fields: labelledControls(form), identifications };
}

function updateIdentificationForm(page: Document) {
  for (const dialog of Array.from(
    page.querySelectorAll("[data-testid='identification-dialog'], .q-dialog"),
  )) {
    const fields = labelledControls(dialog);
    const type = shown(fields.get("identification type")?.value);
    const value = shown(fields.get("identification value")?.value);
    if (type !== undefined && value !== undefined) {
      return {
        type,
        value,
        versionCode: shown(fields.get("version code")?.value),
        endDate: shown(fields.get("plan end date")?.value),
      };
    }
  }
  return undefined;
}

/** Whether the open form is the charted patient's: CHR can keep another patient's form open. */
function samePatient(name: string | undefined, fields: ReadonlyMap<string, LabelledControl>) {
  if (name === undefined) return true;
  const comparable = (text: string) => clean(text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " "));
  const first = fields.get("first name")?.value ?? "";
  const middle = fields.get("middle")?.value ?? fields.get("middle name")?.value ?? "";
  const last = fields.get("last name")?.value ?? "";
  const heading = comparable(name);
  return [`${first} ${last}`, `${first} ${middle} ${last}`, `${last} ${first}`].some(
    (candidate) => comparable(candidate) === heading,
  );
}

/** A province's health card type ("ON"), or undefined for any other identification. */
function cardProvince(type: string): string | undefined {
  const code = type.trim().toUpperCase();
  if (PROVINCES.has(code)) return code;
  return /\bohip\b|ontario health/i.test(type) ? "ON" : undefined;
}

/** "9876543217/AB", "1234-567-890 AB" or "9876543217": the number and its version code. */
function cardNumber(value: string): { number: string; versionCode?: string } | undefined {
  const match = /^([\d\s-]{6,24}?)\s*(?:[/-]?\s*([A-Za-z]{1,2}))?$/u.exec(clean(value));
  if (!match) return undefined;
  const [, digits = "", letters] = match;
  const number = digits.replace(/\D/g, "");
  if (number.length < 6 || number.length > 12) return undefined;
  return letters === undefined ? { number } : { number, versionCode: letters.toUpperCase() };
}

/** CHR's own settings in the Update patient form, not facts about the patient. */
const FORM_SETTINGS = new Set([
  "identifications",
  "notification",
  "notification language",
  "primary location",
]);

/** Contact labels in CHR's patient tags ("Contacts: Email: … / Mobile Phone: …"). */
function contactField(label: string): "email" | "phone" | "otherPhone" | undefined {
  const key = labelKey(label);
  if (/^e-?mail( address)?$/.test(key)) return "email";
  if (/^(mobile|cell)( phone)?$/.test(key)) return "phone";
  if (/^(home )?phone$/.test(key)) return "otherPhone";
  return undefined;
}

function readPatient(page: Document, chart: Element, notImported: NotImportedItem[]) {
  const heading = chartHeading(chart);
  const leftOut = (label: string, value: string, section = PATIENT_DETAILS) =>
    notImported.push({ section, label, value });

  let form = updatePatientForm(page);
  if (form !== undefined && !samePatient(heading.name, form.fields)) {
    leftOut("Update patient form", "Open for another patient, so not read");
    form = undefined;
  }
  const taken = new Set<string>();
  const field = (...labels: string[]) => {
    for (const label of labels) {
      const value = shown(form?.fields.get(label)?.value);
      taken.add(label);
      if (value !== undefined) return value;
    }
    return undefined;
  };

  const demographics: Record<string, unknown> = {};
  const set = (key: string, value: string | undefined) => {
    if (value !== undefined && demographics[key] === undefined) demographics[key] = value;
  };

  // Identity: the form's fields, else the heading's.
  set("legalFirstName", field("first name"));
  set("legalMiddleName", field("middle", "middle name"));
  set("legalLastName", field("last name"));
  if (demographics.legalLastName === undefined && heading.name !== undefined) {
    const named = headingName(heading.name);
    set("legalFirstName", named.legalFirstName);
    set("legalLastName", named.legalLastName);
  }
  set("preferredName", field("preferred name"));
  const birth = field("date of birth") ?? heading.dateOfBirth;
  if (birth !== undefined) {
    const date = readDate(birth);
    if (date === null) leftOut("Date of birth", birth);
    else demographics.dateOfBirth = date;
  }
  const sexShown = field("sex") ?? heading.sex;
  if (sexShown !== undefined) {
    const sex = sexFrom(sexShown);
    if (sex === undefined) leftOut("Sex", sexShown);
    else demographics.administrativeSex = sex;
  }
  // Gender identity is only worth showing when it says something the sex does not.
  const gender = field("gender identity") ?? heading.genderIdentity;
  if (gender !== undefined && sexFrom(gender) !== demographics.administrativeSex) {
    leftOut("Gender identity", gender);
  }

  // Contact: one phone, preferring the cell; the others are shown.
  set("email", field("email address", "email"));
  const phones = [
    ["Cell Phone", field("cell phone", "mobile phone")],
    ["Home Phone", field("home phone")],
    ["Office Phone", field("office phone")],
  ] as const;
  for (const [label, value] of phones) {
    if (value === undefined) continue;
    if (demographics.phone === undefined) demographics.phone = value;
    else leftOut(label, value);
  }
  for (const tag of Array.from(chart.querySelectorAll(".patient-data-tag"))) {
    const tagLabel = shown(tag.querySelector(".tag__label-text")?.textContent);
    const content = shown(tag.querySelector(".tag__content-text")?.textContent);
    if (tagLabel === undefined || content === undefined) continue;
    const parts = content.split(/\s+\/\s+/u);
    const pairs = parts.flatMap((part) => {
      const [, label, value] = /^([^:]{1,48}):\s*(.+)$/u.exec(part) ?? [];
      return label === undefined || value === undefined ? [] : [[clean(label), clean(value)]];
    });
    if (pairs.length !== parts.length) {
      leftOut(tagLabel, content);
      continue;
    }
    for (const [label = "", value = ""] of pairs) {
      const kind = contactField(label);
      const key = kind === "otherPhone" ? "phone" : kind;
      if (key === undefined) leftOut(label, value, tagLabel);
      else if (demographics[key] === undefined) demographics[key] = value;
      else if (clean(String(demographics[key])) !== value) leftOut(label, value, tagLabel);
    }
  }

  const address = defined({
    line1: field("street address", "address"),
    line2: field("additional address"),
    city: field("city"),
    region: field("state/province", "province"),
    postalCode: field("zip/postal code", "postal code"),
    country: field("country"),
  });
  if (Object.keys(address).length > 0) demographics.address = address;
  const contactName = [
    field("emergency contact first name"),
    field("emergency contact last name"),
  ].filter(Boolean);
  const emergencyContact = defined({
    name: contactName.length === 0 ? undefined : contactName.join(" "),
    relationship: field("emergency relationship"),
    phone: field("emergency contact number"),
  });
  if (Object.keys(emergencyContact).length > 0) demographics.emergencyContact = emergencyContact;

  // Whatever else the form holds, as CHR labels it, except CHR's own settings.
  for (const [key, entry] of form?.fields ?? []) {
    const value = shown(entry.value);
    if (value !== undefined && !taken.has(key) && !FORM_SETTINGS.has(key)) {
      leftOut(entry.label, value);
    }
  }

  return {
    demographics: demographics as ExtractedDemographics,
    healthCard: readHealthCard(page, heading, form, leftOut),
  };
}

/**
 * The primary provincial card from the form, else the heading's, with its
 * expiry when the card's own Update identification form is open. Other
 * identifications (a chart number) are shown, not saved.
 */
function readHealthCard(
  page: Document,
  heading: Heading,
  form: PatientForm | undefined,
  leftOut: (label: string, value: string) => void,
): ExtractedHealthCard | undefined {
  const listed = form?.identifications ?? [];
  const cards =
    listed.length > 0 ? listed : heading.card ? [{ ...heading.card, primary: true }] : [];
  const provincial = cards.filter((card) => cardProvince(card.type) !== undefined);
  const chosen = provincial.find((card) => card.primary) ?? provincial[0];
  for (const card of cards) {
    if (card !== chosen) leftOut(card.type, card.value);
  }
  if (chosen === undefined) return undefined;
  const read = cardNumber(chosen.value);
  if (read === undefined) {
    leftOut("Health card", chosen.value);
    return undefined;
  }
  const opened = updateIdentificationForm(page);
  const sameCard =
    opened !== undefined &&
    cardProvince(opened.type) !== undefined &&
    opened.value.replace(/\D/g, "") === read.number;
  const expiry = sameCard && opened.endDate !== undefined ? readDate(opened.endDate) : null;
  if (sameCard && opened.endDate !== undefined && expiry === null) {
    leftOut("Health card expiry", opened.endDate);
  }
  const versionCode =
    (chosen.versionCode ?? (sameCard ? opened.versionCode : undefined))
      ?.replace(/[^A-Za-z]/g, "")
      .toUpperCase() || read.versionCode;
  return {
    number: read.number,
    ...defined({
      versionCode,
      expiryDate: expiry ?? undefined,
      province: cardProvince(chosen.type),
    }),
  };
}

// ---------------------------------------------------------------------------
// The Summary
// ---------------------------------------------------------------------------

interface Cell {
  /** CHR's field name, e.g. `start_at`. */
  readonly field: string;
  /** The column title the page shows, e.g. "Start at". */
  readonly label: string;
  readonly text?: string | undefined;
}

interface Row {
  /** The first named cell with something in it, taken so it is not listed again. */
  take(...names: string[]): (Cell & { readonly text: string }) | undefined;
  /** A named cell with something in it, left to be listed with the rest. */
  peek(...names: string[]): (Cell & { readonly text: string }) | undefined;
  /** The cells nothing took, as "Column: value". */
  rest(): string[];
}

function rowOf(cells: readonly Cell[]): Row {
  const used = new Set<Cell>();
  const matches = (cell: Cell, name: string) =>
    cell.field === name || labelKey(cell.label) === name;
  const find = (take: boolean, names: readonly string[]) => {
    for (const name of names) {
      for (const cell of cells) {
        if (used.has(cell) || !matches(cell, name)) continue;
        if (take) used.add(cell);
        if (cell.text !== undefined) return cell as Cell & { readonly text: string };
      }
    }
    return undefined;
  };
  return {
    take: (...names) => find(true, names),
    peek: (...names) => find(false, names),
    rest: () =>
      cells
        .filter((cell) => !used.has(cell) && cell.text !== undefined)
        .map((cell) => `${cell.label}: ${cell.text}`),
  };
}

/** The classes on CHR's columns and cells that are not a field name. */
const NOT_A_FIELD = new Set(["col", "configurable", "sortable", "sorted", "asc", "desc", "hidden"]);

/** A column's or cell's field: its class besides the generic ones and the locale ("en"). */
function fieldName(element: Element): string | undefined {
  return Array.from(element.classList)
    .filter((name) => !NOT_A_FIELD.has(name) && !/^[a-z]{2}$/u.test(name))
    .at(-1);
}

/** A Summary table's rows: each row a table of its own, its column titles in a head apart. */
function tableRows(content: Element): Cell[][] {
  const titles = new Map<string, string>();
  for (const column of Array.from(
    content.querySelectorAll(".ih-adjustable-table-head .columns > .col"),
  )) {
    const field = fieldName(column);
    const title = clean(column.querySelector(".col-name")?.textContent);
    if (field !== undefined && title !== "") titles.set(field, title);
  }
  return Array.from(content.querySelectorAll(".ih-table-row")).map((row) =>
    Array.from(row.querySelectorAll("td")).flatMap((cell) => {
      const field = fieldName(cell);
      if (field === undefined) return [];
      const label =
        titles.get(field) ?? field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ");
      // The cell's title holds its whole text: "Substance Use → Alcohol" where the cell
      // draws the arrow as an icon.
      const text =
        shown(cell.getAttribute("title")) ??
        shown(cell.querySelector(".content")?.textContent ?? cell.textContent);
      return [{ field, label, text }];
    }),
  );
}

type Section =
  | "medications"
  | "allergies"
  | "problems"
  | "surgical"
  | "familyHistory"
  | "socialHistory"
  | "immunizations"
  | "labs"
  | "visits"
  | "files";

const SECTION_NAMES: ReadonlyMap<string, Section> = new Map([
  ["medications", "medications"],
  ["allergies", "allergies"],
  ["medical_history", "problems"],
  ["surgical_history", "surgical"],
  ["family_history", "familyHistory"],
  ["social_history", "socialHistory"],
  ["immunizations", "immunizations"],
  ["vaccinations", "immunizations"],
  ["lab_results", "labs"],
  ["encounters", "visits"],
  ["files", "files"],
]);

interface SummarySection {
  /** CHR's name for it, e.g. `family_history`. */
  readonly name: string;
  /** The title the page shows, e.g. "Active medications". */
  readonly title: string;
  /** What it holds, shown or folded; absent when CHR never drew it. */
  readonly content: Element | null;
}

function summarySections(chart: Element): SummarySection[] {
  return Array.from(chart.querySelectorAll(".summary-item")).map((item) => {
    // Its name is the class without a hyphen: "summary-item ih-draggable-item medications".
    const name = Array.from(item.classList).find((token) => !token.includes("-")) ?? "";
    const title =
      ownText(item.querySelector(".summary-item-title .toggle-section > span")) ||
      clean(item.querySelector(".summary-item-title")?.textContent) ||
      name.replace(/_/g, " ");
    return { name, title, content: item.querySelector(".summary-item-content-area") };
  });
}

function sectionOf(section: SummarySection): Section | undefined {
  return (
    SECTION_NAMES.get(section.name) ??
    (/immuni[sz]ation|vaccin/i.test(section.title) ? "immunizations" : undefined)
  );
}

function statusWord(text: string | undefined): "inactive" | "resolved" | "completed" | undefined {
  const value = (text ?? "").toLowerCase();
  if (/\bresolved\b/.test(value)) return "resolved";
  if (/\b(completed|finished)\b/.test(value)) return "completed";
  if (/\b(inactive|discontinued|stopped|d\/c|past|historical|previous)\b/.test(value))
    return "inactive";
  return undefined;
}

function conditionStatus(text: string | undefined): "active" | "inactive" | "resolved" | undefined {
  const word = statusWord(text);
  if (word === "completed") return "resolved";
  if (word !== undefined) return word;
  return /\bactive\b/i.test(text ?? "") ? "active" : undefined;
}

/**
 * A prescription's status: the page's own word for it, else "completed" once
 * its end date has passed (CHR's "Active medications" lists finished courses).
 */
function medicationStatus(
  text: string | undefined,
  ended: string | undefined,
  today: string,
): ExtractedMedication["status"] {
  const word = statusWord(text);
  if (word === "resolved") return "inactive";
  if (word !== undefined) return word;
  if (/\bactive\b/i.test(text ?? "")) return "active";
  const end = ended === undefined ? null : readDate(ended);
  return end !== null && end < today ? "completed" : undefined;
}

function severityFrom(text: string | undefined): ExtractedAllergy["severity"] {
  const value = (text ?? "").toLowerCase();
  if (/\bsevere\b|\banaphyla/.test(value)) return "severe";
  if (/\bmoderate\b/.test(value)) return "moderate";
  if (/\bmild\b/.test(value)) return "mild";
  return undefined;
}

const RELATIONS: ReadonlyArray<readonly [RegExp, ExtractedFamilyHistory["relation"]]> = [
  [/^(mother|mom|mum)$/i, "mother"],
  [/^(father|dad)$/i, "father"],
  [/^sister$/i, "sister"],
  [/^brother$/i, "brother"],
  [/^daughter$/i, "daughter"],
  [/^son$/i, "son"],
  [/^maternal grand ?mother$/i, "maternal_grandmother"],
  [/^maternal grand ?father$/i, "maternal_grandfather"],
  [/^paternal grand ?mother$/i, "paternal_grandmother"],
  [/^paternal grand ?father$/i, "paternal_grandfather"],
  [/^(maternal |paternal )?aunt$/i, "aunt"],
  [/^(maternal |paternal )?uncle$/i, "uncle"],
  [/^cousin$/i, "cousin"],
];

const SOCIAL_KINDS: ReadonlyArray<readonly [RegExp, ExtractedSocialHistory["kind"]]> = [
  [/smok|tobacco|cigar|vap/i, "tobacco"],
  [/alcohol|etoh|drink/i, "alcohol"],
  [/cannabis|marijuana|thc/i, "cannabis"],
  [/drug|substance|opioid|cocaine/i, "substance"],
  [/occupation|work|job|employ|retired/i, "occupation"],
  [/hous|lives|living/i, "housing"],
  [/exercise|activity|physical/i, "exercise"],
  [/diet|nutrition/i, "diet"],
];

function socialStatus(text: string): ExtractedSocialHistory["status"] {
  if (/\b(never|denies|none|non[- ]?(drinker|smoker|user)|abstains?|abstinent)\b/i.test(text))
    return "never";
  if (/\b(former|ex-|quit|stopped|previous|past|retired)/i.test(text)) return "former";
  if (
    /\b(current|daily|smokes|smoker|drinks|drinker|occasional|social|working|employed|uses)\b/i.test(
      text,
    )
  )
    return "current";
  return "unknown";
}

/**
 * A row read as an entry, with what it held that the entry has no place for,
 * or a row that cannot be one, shown as the page wrote it.
 */
type Read<T> =
  | { readonly entry: T; readonly leftOver?: readonly string[] }
  | { readonly shownAs: NotImportedItem };

function medicationFrom(row: Row, today: string): Read<ExtractedMedication> | undefined {
  const display = row.take("name", "medication", "drug")?.text;
  if (display === undefined) return undefined;
  const sig = row.take("dose", "sig", "directions", "instructions")?.text;
  const status = medicationStatus(
    row.take("status")?.text,
    row.peek("end_at", "end at", "end date")?.text,
    today,
  );
  // The prescription's dates have no place on BobDoc's medication list; they are shown.
  return { entry: { display, ...defined({ sig, status }) }, leftOver: row.rest() };
}

function allergyFrom(row: Row): Read<ExtractedAllergy> | undefined {
  const display = row.take("name", "allergen", "allergy", "substance")?.text;
  if (display === undefined) return undefined;
  const reaction = row.take("reaction", "reactions")?.text;
  const severity = severityFrom(row.take("severity")?.text ?? reaction);
  const status = conditionStatus(row.take("status")?.text);
  return { entry: { display, ...defined({ reaction, severity, status }) }, leftOver: row.rest() };
}

/** A date for a field that takes one; an unreadable one goes to the note as the page wrote it. */
function dated(cell: (Cell & { readonly text: string }) | undefined, note: string[]) {
  if (cell === undefined) return undefined;
  const date = readDate(cell.text);
  if (date === null) note.push(`${cell.label}: ${cell.text}`);
  return date ?? undefined;
}

function problemFrom(row: Row, surgical: boolean): Read<ExtractedProblem> | undefined {
  const display = row.take(
    "diagnoses",
    "diagnosis",
    "problem",
    "condition",
    "procedures",
    "name",
  )?.text;
  if (display === undefined) return undefined;
  const note: string[] = [];
  const status = surgical ? "resolved" : conditionStatus(row.take("status")?.text);
  const onsetDate = dated(
    surgical
      ? row.take("procedure_date", "procedure date", "date")
      : row.take("start_date", "onset date", "onset"),
    note,
  );
  note.push(...row.rest());
  return {
    entry: {
      display,
      ...(surgical ? { category: "surgical" as const } : {}),
      ...defined({ onsetDate, note: noteFrom(note), status }),
    },
  };
}

function familyFrom(row: Row): Read<ExtractedFamilyHistory> | undefined {
  const display = row.take("diagnoses", "diagnosis", "condition", "problem")?.text;
  if (display === undefined) return undefined;
  const note: string[] = [];
  const relative = row.take("relationship", "relation", "relative");
  const relation =
    relative === undefined
      ? undefined
      : RELATIONS.find(([pattern]) => pattern.test(relative.text))?.[1];
  if (relative !== undefined && relation === undefined) {
    note.push(`${relative.label}: ${relative.text}`);
  }
  const age = row.take("age_at_onset", "age at onset", "onset age", "age");
  const years = age === undefined ? undefined : /^(\d{1,3})\b/.exec(age.text)?.[1];
  const onsetAge = years === undefined || Number(years) > 130 ? undefined : Number(years);
  if (age !== undefined && onsetAge === undefined) note.push(`${age.label}: ${age.text}`);
  note.push(...row.rest());
  return {
    entry: {
      relation: relation ?? "other",
      display,
      ...defined({ onsetAge, note: noteFrom(note) }),
    },
  };
}

function socialFrom(row: Row): Read<ExtractedSocialHistory> | undefined {
  const type = row.take("type", "category", "kind");
  const said = row.take("status", "use", "description")?.text;
  if (type === undefined && said === undefined) return undefined;
  // CHR nests its types: "Substance Use → Alcohol".
  const leaf = type === undefined ? "" : clean(type.text.split(/→|>|»/u).at(-1));
  const kind =
    SOCIAL_KINDS.find(([pattern]) => pattern.test(leaf))?.[1] ??
    SOCIAL_KINDS.find(([pattern]) => pattern.test(type?.text ?? ""))?.[1] ??
    "other";
  const detail =
    kind === "other" && leaf !== ""
      ? [leaf, said].filter(Boolean).join(": ")
      : (said ?? (leaf || type?.text) ?? "");
  const note: string[] = [];
  const startedOn = dated(row.take("start_date", "start date", "started"), note);
  const endedOn = dated(row.take("end_date", "end date", "ended", "quit date"), note);
  note.push(...row.rest());
  return {
    entry: {
      kind,
      status: socialStatus(said ?? ""),
      detail: detail.slice(0, 500),
      ...defined({ startedOn, endedOn, note: noteFrom(note) }),
    },
  };
}

function immunizationFrom(row: Row, title: string): Read<ExtractedImmunization> | undefined {
  const display = row.take("name", "vaccine", "immunization", "immunisation", "agent")?.text;
  if (display === undefined) return undefined;
  const given = row.take(
    "administered_at",
    "administered_on",
    "date_given",
    "given_at",
    "date",
    "date given",
    "date administered",
    "administered",
  );
  const administeredOn = given === undefined ? null : readDate(given.text);
  if (administeredOn === null) {
    // BobDoc keeps an immunization only with the day it was given.
    return { shownAs: { section: title, label: display, value: given?.text ?? "No date given" } };
  }
  const lotNumber = row.take("lot_number", "lot", "lot number", "lot #")?.text;
  return {
    entry: { display, administeredOn, ...defined({ lotNumber, note: noteFrom(row.rest()) }) },
  };
}

function resultStatus(text: string | undefined) {
  const value = (text ?? "").toLowerCase();
  if (/final/.test(value)) return "final" as const;
  if (/prelim|pending|partial/.test(value)) return "preliminary" as const;
  if (/correct|amend/.test(value)) return "corrected" as const;
  return undefined;
}

/** CHR's Summary lists each result with its date beside the name and its flag after the value. */
function labResults(content: Element, title: string, notImported: NotImportedItem[]) {
  const results: ExtractedLabResult[] = [];
  for (const row of Array.from(content.querySelectorAll("tr.result-item"))) {
    const nameCell = row.querySelector(".result-name");
    const display = ownText(nameCell) || undefined;
    const value =
      shown(row.querySelector(".result-value-item .value")?.textContent) ??
      shown(row.querySelector(".result-value-item")?.textContent);
    if (display === undefined || value === undefined) {
      const said = clean(row.textContent);
      if (said !== "") notImported.push({ section: title, label: "Result", value: said });
      continue;
    }
    const date = shown(row.querySelector(".collected-at")?.textContent);
    const range = shown(row.querySelector(".ref-range-units .extra-detail")?.textContent);
    const unit = shown(row.querySelector(".ref-range-units .units")?.textContent);
    const marked = Array.from(row.querySelector(".result-value-item")?.classList ?? [])
      .find((name) => /^abnormal-/.test(name) && name !== "abnormal-none")
      ?.slice("abnormal-".length)
      .toUpperCase();
    const flag = shown(row.querySelector("td.abnormal-flag")?.textContent) ?? marked;
    const bounds =
      range === undefined
        ? undefined
        : /^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)$/u.exec(range);
    const [, low, high] = bounds ?? [];
    const reference =
      low !== undefined && high !== undefined
        ? { referenceLow: low, referenceHigh: high }
        : range === undefined
          ? {}
          : { referenceText: range };
    results.push({
      collectedOn: date === undefined ? "" : (readDate(date) ?? date),
      ...defined({ panelName: shown(row.querySelector(".section-name")?.textContent) }),
      result: {
        display,
        ...(/^-?\d+(\.\d+)?$/.test(value) ? { valueNumeric: value } : { valueText: value }),
        ...reference,
        ...defined({
          unit,
          flag: flag?.slice(0, 16),
          resultStatus: resultStatus(shown(row.querySelector("td.status")?.textContent)),
        }),
      },
    });
  }
  return results;
}

type NoteSections = NonNullable<NonNullable<ExtractedVisit["note"]>["sections"]>;

/** CHR's note headings onto the legacy note's own sections; the rest stay in its text. */
const NOTE_SECTIONS: ReadonlyArray<readonly [RegExp, keyof NoteSections]> = [
  [/^(history( of present illness)?|hpi|subjective)$/i, "history"],
  [/^(examination|physical exam(ination)?|exam|objective)$/i, "physicalExam"],
  [/^(assessment (and|&) plan|assessment|plan|impression)$/i, "assessmentPlan"],
  [/^(prescriptions|medications prescribed|rx)$/i, "medicationsPrescribed"],
  [/^vitals?( signs)?$/i, "vitals"],
];

/**
 * CHR's Summary lists every encounter with its note: a title ("PI: Walk In (In
 * Clinic)"), the note's sections under their headings, and a footer naming who
 * wrote or signed it and the day the patient was seen. Each becomes a past
 * visit, dated at noon that day (CHR shows no time); one without a note is
 * kept as a visit alone.
 */
function visitsFrom(content: Element, title: string, notImported: NotImportedItem[]) {
  const visits: ExtractedVisit[] = [];
  for (const item of Array.from(content.querySelectorAll(".encounter-item"))) {
    const heading = clean(item.querySelector(".open-encounter")?.textContent);
    const footerElement = item.querySelector(".box-footer-wrap");
    const footer = ownText(footerElement) || clean(footerElement?.textContent);
    // "Created by Dr. Robin Okafor MD on Sep, 27 2026 - Seen on Sep, 27 2026"; "Signed by …".
    const said = /^(created|signed)\s+by\s+(.+?)\s+on\s+(.+?)\s+-\s+seen\s+on\s+(.+)$/iu.exec(
      footer,
    );
    const [, verb = "", author = "", written = "", seenOn = ""] = said ?? [];
    const occurredAt = collectedAtFor(readDate(seenOn) ?? "");
    if (occurredAt === null) {
      notImported.push({ section: title, label: heading || "Visit", value: footer || "No date" });
      continue;
    }
    const sections = Array.from(item.querySelectorAll(".sections-wrapper > .section")).flatMap(
      (section) => {
        const copy = section.cloneNode(true) as Element;
        const name = clean(copy.querySelector("h1, h2, h3")?.textContent);
        copy.querySelector("h1, h2, h3")?.remove();
        const body = blockText(copy);
        return body === "" ? [] : [{ name, body }];
      },
    );
    const mapped: Partial<Record<keyof NoteSections, string>> = {};
    for (const { name, body } of sections) {
      const key = NOTE_SECTIONS.find(([pattern]) => pattern.test(name))?.[1];
      if (key !== undefined)
        mapped[key] = mapped[key] === undefined ? body : `${mapped[key]}\n\n${body}`;
    }
    const noted = sections.length > 0;
    const signed = noted && /^signed$/i.test(verb);
    const authoredAt = collectedAtFor(readDate(written) ?? "");
    const writer = author.slice(0, 160) || undefined;
    visits.push({
      kind: noted ? "note" : "appointment",
      occurredAt,
      timeKnown: false,
      status: "completed",
      noteSignedInSource: signed,
      ...defined({
        sourceStatus: noted ? (signed ? "Signed" : "Not signed") : undefined,
        reason: heading.replace(/^PI:\s*/iu, "").slice(0, 500) || undefined,
        provider:
          writer === undefined
            ? undefined
            : { display: writer, kind: /^dr\.?\s/i.test(writer) ? "physician" : "unknown" },
        note: !noted
          ? undefined
          : {
              text: sections
                .map(({ name, body }) => (name === "" ? body : `${name}\n${body}`))
                .join("\n\n")
                .slice(0, 100_000),
              ...(Object.keys(mapped).length === 0 ? {} : { sections: mapped }),
              ...defined({ authorDisplay: writer, authoredAt: authoredAt ?? undefined }),
            },
      }),
    });
  }
  return visits;
}

/** CHR's Summary lists the chart's files without their contents: each is shown, not imported. */
function filesListed(content: Element, title: string, notImported: NotImportedItem[]) {
  for (const cells of tableRows(content)) {
    const row = rowOf(cells);
    const name = row.take("name", "file name")?.text ?? "File";
    notImported.push({
      section: title,
      label: name,
      value: row.rest().join(" · ") || "File",
      listedFile: true,
    });
  }
}

function readSummary(chart: Element, today: string, notImported: NotImportedItem[]) {
  const found = {
    medications: [] as ExtractedMedication[],
    allergies: [] as ExtractedAllergy[],
    problems: [] as ExtractedProblem[],
    familyHistory: [] as ExtractedFamilyHistory[],
    socialHistory: [] as ExtractedSocialHistory[],
    immunizations: [] as ExtractedImmunization[],
    labs: [] as ExtractedLabResult[],
    visits: [] as ExtractedVisit[],
  };
  for (const section of summarySections(chart)) {
    const { content, title } = section;
    if (content === null) continue; // CHR never drew it.
    const kind = sectionOf(section);
    if (kind === undefined) {
      if (clean(content.textContent) !== "" || content.querySelector("[data-bobdoc-frame]")) {
        notImported.push({ section: title, label: title, value: "The whole section" });
      }
      continue;
    }
    if (kind === "labs") {
      found.labs.push(...labResults(content, title, notImported));
      continue;
    }
    if (kind === "visits") {
      found.visits.push(...visitsFrom(content, title, notImported));
      continue;
    }
    if (kind === "files") {
      filesListed(content, title, notImported);
      continue;
    }
    for (const cells of tableRows(content)) {
      const row = rowOf(cells);
      const keep = <T>(list: T[], read: Read<T> | undefined) => {
        if (read === undefined) return false;
        if ("shownAs" in read) {
          notImported.push(read.shownAs);
          return true;
        }
        list.push(read.entry);
        const leftOver = read.leftOver ?? [];
        if (leftOver.length > 0) {
          const named = (read.entry as { display?: string }).display ?? title;
          notImported.push({ section: title, label: named, value: leftOver.join(" · ") });
        }
        return true;
      };
      const kept =
        kind === "medications"
          ? keep(found.medications, medicationFrom(row, today))
          : kind === "allergies"
            ? keep(found.allergies, allergyFrom(row))
            : kind === "problems" || kind === "surgical"
              ? keep(found.problems, problemFrom(row, kind === "surgical"))
              : kind === "familyHistory"
                ? keep(found.familyHistory, familyFrom(row))
                : kind === "socialHistory"
                  ? keep(found.socialHistory, socialFrom(row))
                  : keep(found.immunizations, immunizationFrom(row, title));
      const said = cells
        .filter((cell) => cell.text !== undefined)
        .map((cell) => `${cell.label}: ${cell.text}`)
        .join(" · ");
      if (!kept && said !== "") {
        notImported.push({ section: title, label: "Could not be read as an entry", value: said });
      }
    }
  }
  return found;
}

/**
 * Reads a captured TELUS CHR chart. `capturedAt` dates the capture, to tell a
 * finished prescription from a current one. Nothing on the page is saved until
 * the physician reviews it.
 */
export function parseTelusChrPage(
  html: string,
  { capturedAt }: { readonly capturedAt?: string } = {},
): ExtractedChartPage {
  const page = parseCapturedPage(html);
  const chart =
    CHART.map((selector) => page.querySelector(selector)).find((found) => found !== null) ??
    page.body ??
    page.documentElement;
  const notImported: NotImportedItem[] = [];
  const { demographics, healthCard } = readPatient(page, chart, notImported);
  const found = readSummary(chart, captureDay(capturedAt), notImported);
  return checkedChartPage({
    source: "telus-chr",
    demographics,
    ...(healthCard ? { healthCard } : {}),
    ...found,
    files: [],
    notImported,
  });
}

// ---------------------------------------------------------------------------
// The patient's files, as the bookmark brought them
// ---------------------------------------------------------------------------

const FILES = "Files";

/**
 * A file's kind, from CHR's own words for it (its type and report class).
 * Unrecognised words leave it unclassed; nothing is guessed from its name.
 */
const FILE_CATEGORIES: ReadonlyArray<
  readonly [RegExp, NonNullable<ExtractedFile["categoryHint"]>]
> = [
  [/\blab|patholog|microbiolog|bloodwork/iu, "lab"],
  [/imaging|radiolog|x-?ray|ultrasound|\bmri\b|\bct\b|mammogra|\becho/iu, "imaging"],
  [/consult|specialist/iu, "consult"],
  [/discharge|hospital|admission/iu, "discharge"],
];

function fileCategory(file: CapturedFile): ExtractedFile["categoryHint"] {
  const words = [file.subType, file.reportSubclass].filter(Boolean).join(" ");
  return FILE_CATEGORIES.find(([pattern]) => pattern.test(words))?.[1];
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

/** The day CHR gave, in Ontario: a date, a date-time, or CHR's own "Sep, 01 2025 9:15AM". */
function fileDay(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (DATE_ONLY.test(value)) return collectedAtFor(value) === null ? undefined : value;
  if (DATE_TIME.test(value)) {
    return Number.isNaN(Date.parse(value)) ? undefined : captureDay(value);
  }
  return readDate(value) ?? undefined;
}

/** The moment CHR gave; a bare date becomes that day's noon in Ontario. */
function fileMoment(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (DATE_TIME.test(value)) {
    const moment = Date.parse(value);
    return Number.isNaN(moment) ? undefined : new Date(moment).toISOString();
  }
  const day = fileDay(value);
  return day === undefined ? undefined : (collectedAtFor(day) ?? undefined);
}

const EXTENSIONS: Readonly<Record<ExtractedFile["mimeType"], RegExp>> = {
  "application/pdf": /\.pdf$/iu,
  "image/jpeg": /\.jpe?g$/iu,
  "image/png": /\.png$/iu,
};
const EXTENSION_FOR: Readonly<Record<ExtractedFile["mimeType"], string>> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
};
const UNSAFE_IN_FILE_NAMES = /[\\/:*?"<>|\p{Cc}]+/gu;

/** The file's own name, made safe to store and download, ending the way its type does. */
function storedFileName(file: CapturedFile, title: string): string {
  const named =
    clean(file.fileName.replace(UNSAFE_IN_FILE_NAMES, " ")) ||
    clean(title.replace(UNSAFE_IN_FILE_NAMES, " "));
  const whole = EXTENSIONS[file.mimeType].test(named)
    ? named
    : `${named.replace(/\.[a-z0-9]{1,5}$/iu, "") || "file"}.${EXTENSION_FOR[file.mimeType]}`;
  if (whole.length <= 240) return whole;
  const ending = whole.slice(whole.lastIndexOf("."));
  return `${whole.slice(0, 240 - ending.length)}${ending}`;
}

/**
 * The patient's files the bookmark brought from CHR (CHR_FILES_SOURCE), read
 * into the documents BobDoc files: CHR's name for each as its title, the day
 * CHR files it under (when a lab was collected, else when the clinic received
 * or uploaded it), its description and the facility it came from as notes,
 * and its kind when CHR's report class names one. Files left behind are
 * listed with why.
 */
export function chrBroughtFiles(brought: CapturedFiles): BroughtFiles {
  if ("failed" in brought) return { failed: brought.failed };
  const files = brought.items.map((file): ExtractedFile => {
    const title = clean(file.name).slice(0, 240) || "Untitled file";
    const notes = [file.comment, file.sourceFacility ? `From ${file.sourceFacility}` : undefined]
      .map((part) => clean(part))
      .filter((part) => part !== "")
      .join("\n")
      .slice(0, 4000);
    return {
      reference: `file:${file.id}`,
      title,
      fileName: storedFileName(file, title),
      mimeType: file.mimeType,
      bytes: file.bytes,
      ...defined({
        effectiveDate:
          fileDay(file.collectedAt) ?? fileDay(file.receivedAt) ?? fileDay(file.createdAt),
        receivedAt:
          fileMoment(file.receivedAt) ?? fileMoment(file.addedAt) ?? fileMoment(file.createdAt),
        reviewedAt: fileMoment(file.reviewedAt),
        notes: notes === "" ? undefined : notes,
        categoryHint: fileCategory(file),
        archived: file.archived === true ? true : undefined,
      }),
    };
  });
  const leftBehind = brought.skipped.map(
    (file): NotImportedItem => ({
      section: FILES,
      label: clean(file.name).slice(0, 240) || "Untitled file",
      value: file.reason,
    }),
  );
  return { files, leftBehind };
}
